import { Autocomplete, Button, ButtonGroup, Checkbox, CreateCustomerModal, CreditCardModal, CurrencyInput, DatePicker, Grid, Input, Layout, NumericInput, Typography, Loader, Alert } from "../../../components";
import { Paper } from "../../../components";
import { useFormik } from "formik";
import { CreateOrderValidationSchema, requiresPassengerBirthDate } from "@next-pontos/validations";
import { useAPI, useAxios, useToggle } from "../../../hooks";
import {useEffect, useMemo, useCallback, useState, Fragment, useContext} from 'react'
import useSWR from 'swr'
import { Box, FormControlLabel, IconButton, Link, Tooltip } from "@mui/material";
import { composeSummary, formatCNPJ, formatCPF, formatDate, formatMoney, formatNumber, momentFromCalendarDate, shouldKeepCanceledCpfAsUsed } from "../../../utils";
import {  useParams } from "react-router-dom";
import API from "../../../API";
import moment from "moment";
import { AIRLINES_OPTIONS, COMPANY_OPTIONS } from "../../../constants";
import currency from "currency.js";
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import AuthContext from "../../../context/auth";
import { APP_NAME } from "@next-pontos/settings";
import { Add, ContentCopy, Done } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import {Modal, BoardguardMonitorModal} from '../../../components'

export function NewOrder () { 
    const [creditCardModalVisibility, creditCardModalVisibilityControls] = useToggle(false)
    const { me } = useContext(AuthContext)
    const params = useParams()
    const bid = useSWR(`/bid/${params.id}`, (route) => params.id ? API.get(route).then(result => result.data): null)
    const cards = useSWR(`/card`, (route) => API.get(route).then(result => result.data))
    const [customers, refetchCustomers] = useAxios(`/customer`)
    const [coupons] = useAxios(`/coupon`)
    const [createCustomerModalVisibility, createCustomerModalVisibilityControls] = useToggle(false)
    const [orderAgain, orderAgainControls] = useToggle(false)
    const [boardguardModal, boardguardModalControls] = useToggle(false)
    const [passwordData, setPasswordData] = useState<null | { password: string }>(null)
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const passwordApi = useAPI()
    const orderApi = useAPI()
    const validateHP = useAPI()

    const [lastOrderId, setLastOrderId] = useState(0)
    
    const formik = useFormik({
        initialValues: {
            flight_date: '',
            airline: '',
            reservation_code: '',
            reservation_code_2: '',
            amount: '',
            fees: '',
            additional_cost: '0',
            card_id: '',
            sell_price: '',
            pax: '',
            author_name: me?.name || '',
            passengers: [{ name: '', surname: '', birth_date: '' }],
            origin_airport_code: '',
            customer_id: '',
            description: '',
            to_receive: false,
            income_due_date: null,
            cost_per_k: '',
            coupon_id: '',
            hp_id: '',
            company: '',
        },
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                const { status, data } = await orderApi.post<{ id: number, boardguard_supported?: boolean }>(`/bid/${bid?.data?.id}/order`, values)

                if (status < 200 || status >= 300 || !data?.id) {
                    return
                }

                setLastOrderId(data.id)
                enqueueSnackbar('Emissão cadastrada com sucesso!', {variant: 'success'})

                if (data.boardguard_supported) {
                    boardguardModalControls.setTrue()
                } else {
                    orderAgainControls.setTrue()
                }
            } finally {
                setIsSubmitting(false);
            }
        },
        validateOnMount: true,
        validationSchema: CreateOrderValidationSchema
    })

    useEffect(() => {
        if (me?.name) {
            formik.setFieldValue('author_name', me.name)
        }
    }, [me?.name])

    useEffect(() => {
        if (bid?.data?.company) {
            void formik.setFieldValue('company', bid.data.company, false).then(() => {
                void formik.validateForm({ ...formik.values, company: bid.data.company })
            })
        }
    }, [bid?.data?.company])
    
    const CARDS_OPTIONS = useMemo(() => {
        return (cards?.data || []).map((card: { id: number, last_four_digits: string, brand: string, alias: string }) => ({
            value: card.id,
            label: `**** ${card.last_four_digits} (${card.alias})`
        }))
    }, [cards])

    const totalCost = useMemo(() => {
        const amount_k = currency(formik.values.amount || 0, {precision: 3}).divide(1000).value
        const price_per_k = bid?.data?.price_per_k || 0
        const fees = formik.values.fees || 0
        const additional_cost = formik.values.additional_cost || 0

        const miles_cost = currency(amount_k, {precision: 3}).multiply(price_per_k)
        const final_cost = miles_cost.add(fees).add(additional_cost)
        console.log(price_per_k, additional_cost, amount_k, fees, miles_cost.value,  final_cost.value)
        return final_cost.value
    }, [formik, bid])

    const airlineOptions = useMemo(() => {
        return AIRLINES_OPTIONS.map(item => ({
            value: item.label,
            label: item.label
        }))
    }, [])

    const handleRevealPassword = useCallback(async () => {
        const passwordResponse = await passwordApi.get(`/account/${bid?.data?.account_id}/password`)
        setPasswordData(passwordResponse.data)
    }, [passwordApi, bid?.data?.account_id])

    const customersOptions = (customers?.data || []).map((customer: {name: string, id: string, is_business: boolean, document_number: string }) => ({
        label: `${customer.name} (${customer.is_business ? formatCNPJ(customer.document_number) : formatCPF(customer.document_number)})`,
        value: customer.id,
    }))

    const milesAvailable = useMemo(() => {
        const usedMiles = bid?.data?.orders?.filter((item: {confirmation: string}) => item.confirmation === 'PENDING' || item.confirmation === 'CONFIRMED')?.map((item: {miles: number}) => item.miles)?.reduce((prev: number, curr: number) => prev + curr, 0)
    
        return currency(bid?.data?.amount).subtract(usedMiles).value
    }, [bid?.data])

    const { amount, sell_price, fees, additional_cost, cost_per_k } = formik.values
    const { setErrors }  = formik

    useEffect(() => {
        if (Number(amount) > Number(milesAvailable)) {
            setErrors({ amount: 'A quantidade de milhas deve ser menor que o disponível na oferta' })
        }
    }, [amount, milesAvailable, setErrors])

    const messageText = encodeURI(`Olá, ${bid?.data?.user?.name}! Tudo bem? Me chamo ${me?.name} e estou entrando em contato para falar da sua oferta número ${bid?.data?.id} na ${APP_NAME}.`)

    const soldCostPerK = useMemo(() => {
        const totalMilesCost = currency(sell_price || 0).subtract(fees || 0).subtract(additional_cost || 0).value
        
        return currency(totalMilesCost).divide(currency(amount || 0, {precision: 3}).divide(1000).value).value || 0
    }, [additional_cost, amount, fees, sell_price])

    useEffect(() => {
        if (me?.role === 'ADMIN' && !bid.isLoading && !passwordData && bid.data) {
            handleRevealPassword()
        }
    }, [me, bid])

    const usedMiles = useMemo(() => {
        return (bid?.data?.orders || []).filter((item: {confirmation: string}) => item.confirmation !== 'CANCELLED').reduce((prev: number, curr: {miles: number}) => prev += curr.miles, 0)
    }, [bid?.data])

    const usedPax = useMemo(() => {
        return (bid?.data?.orders || [])
            .filter((item: { confirmation: string }) =>
                shouldKeepCanceledCpfAsUsed(bid?.data?.company) || item.confirmation !== 'CANCELLED'
            )
            .reduce((prev: number, curr: {pax: number}) => prev += curr.pax, 0)
    }, [bid?.data?.orders, bid?.data?.company])

    const availableMiles = useMemo(() => {
        return currency(bid?.data?.amount).subtract(usedMiles).value
    }, [usedMiles, bid?.data?.amount])

    const couponsOptions: Array<{label: string, value: number | string}> = useMemo(() => {
        return (coupons.data || []).map((item: {code: string, id: number, discount_method: 'MONEY' | 'PERCENTAGE', discount: number}) => ({
            label: `${item.code} (${item.discount_method === 'MONEY' ? formatMoney(Number(item.discount)) : `${formatNumber(Number(item.discount))}%`})`,
            value: item.id
        }))
    }, [coupons])

    const couponDiscount = useMemo(() => {
        const coupon = (coupons.data || []).find((item: {id: string}) => item.id === formik.values.coupon_id)

        if (!coupon) {
            return 0
        }

        const discount = coupon?.discount_method === 'MONEY' ? coupon?.discount : currency(formik.values.sell_price).multiply(1 - currency(coupon?.discount).divide(100).value).value

        return discount || 0
    }, [formik.values.coupon_id, coupons, formik.values.sell_price])

    useEffect(() => {
        const k = currency(amount || 0, {precision: 3}).divide(1000).value

        const totalMiles = currency(k, {precision: 3}).multiply(cost_per_k || 0).value

        const total = currency(totalMiles, {precision: 3}).add(additional_cost || 0).add(fees || 0).value

        formik.setFieldValue('sell_price', total)
    }, [amount, fees, additional_cost, cost_per_k])
    
    return (
        <Layout>
            {boardguardModal && (
                <BoardguardMonitorModal
                    open={boardguardModal}
                    orderId={lastOrderId}
                    onClose={() => {
                        boardguardModalControls.setFalse()
                        orderAgainControls.setTrue()
                    }}
                />
            )}
            {orderAgain && <Modal
                title="Atenção!"
                open={orderAgain}
                onConfirm={() => {
                    window.location.reload()
                }}
                confirmLabel="Sim"
                onCancel={() => {
                    window.location.replace(`/admin/emissoes`)
                }}
                cancelLabel="Não"
            >
                <div>
                    <Alert
                        severity="success"
                        action={
                            <Button color="inherit" size="small" variant="text" onClick={() => {
                                const summary = composeSummary({
                                    id: lastOrderId,
                                    reservationCode: formik.values.reservation_code,
                                    fees: Number(formik.values.fees),
                                    miles: Number(formik.values.amount),
                                    total: Number(formik.values.sell_price),
                                    additional_cost: Number(formik.values.additional_cost || 0),
                                    company: COMPANY_OPTIONS.find(item => item.value === bid.data.company)?.label || ''
                                })

                                navigator.clipboard.writeText(summary)

                                enqueueSnackbar('Resumo copiado para a área de transferência.', {variant: 'success'})
                            }}>
                                COPIAR RESUMO
                            </Button>
                        }
                    >
                        Emissão realizada com sucesso.
                    </Alert>
                    <Typography mt={2}>
                        Você deseja fazer outra emissão nesta mesma oferta?
                    </Typography>
                </div>
            </Modal>}
            
            {bid.isLoading ? (
                <div style={{
                    display: 'flex',
                    flex: '1',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 64px)'
                }}>
                    <Loader />
                </div>
            ) : (
                <Grid container spacing={4} minHeight={"calc(100vh - 64px)"} alignItems="flex-start" paddingTop={5} paddingX={4}>
                    <Grid item xs={12} lg={6}>
                        <Paper elevation={1} style={{border: '1px solid #e0e0e0'}}>
                            <form onSubmit={formik.handleSubmit}>
                                <Grid container padding={3} spacing={1}>
                                    <Grid item xs={12}>
                                        <Typography variant="h5" gutterBottom>Cadastrar Emissão</Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Dados do Cliente</Typography>
                                    </Grid>
                                    <Grid item xs={11}>
                                        <Autocomplete 
                                            label="Cliente"
                                            name="customer_id"
                                            onChange={({value}) => formik.setFieldValue('customer_id', value, true)}
                                            options={customersOptions}
                                            onBlur={formik.handleBlur}
                                            value={customersOptions.find((item: { value: string }) => item.value === formik.values.customer_id) || null}
                                            helperText={(formik.touched.customer_id || formik.submitCount > 0) && formik.errors.customer_id}
                                            error={!!(formik.touched.customer_id || formik.submitCount > 0) && !!formik.errors.customer_id}
                                        />
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Button 
                                            onClick={createCustomerModalVisibilityControls.setTrue}
                                            sx={{height: '100%'}}
                                        >
                                            <Add />
                                        </Button>
                                    </Grid>

                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Detalhes do Voo</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <DatePicker 
                                            label="Data do Voo"
                                            value={momentFromCalendarDate(formik.values.flight_date)}
                                            onChange={(value) => formik.setFieldValue('flight_date', value?.isValid() ? value.format('YYYY-MM-DD') : '', true)}
                                            helperText={(formik.touched.flight_date || formik.submitCount > 0) && formik.errors.flight_date}
                                            error={!!(formik.touched.flight_date || formik.submitCount > 0) && !!formik.errors.flight_date}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Autocomplete 
                                            label="Companhia"
                                            onChange={({value}) => formik.setFieldValue('airline', value, true)}
                                            options={airlineOptions}
                                            onBlur={formik.handleBlur}
                                            value={airlineOptions.find(item => item.value === formik.values.airline)}
                                            helperText={formik.touched.airline && formik.errors.airline}
                                            error={!!formik.touched.airline && !!formik.errors.airline}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Emissor"
                                            name="author_name"
                                            onChange={formik.handleChange}
                                            value={formik.values.author_name}
                                            helperText={formik.touched.author_name && formik.errors.author_name}
                                            error={!!formik.touched.author_name && !!formik.errors.author_name}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Localizador 1"
                                            name="reservation_code"
                                            onChange={event => formik.setFieldValue('reservation_code', event.target.value.toUpperCase().trim())}
                                            value={formik.values.reservation_code}
                                            helperText={formik.touched.reservation_code && formik.errors.reservation_code}
                                            error={!!formik.touched.reservation_code && !!formik.errors.reservation_code}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input 
                                            label="Localizador 2 (opcional)"
                                            name="reservation_code_2"
                                            onChange={event => formik.setFieldValue('reservation_code_2', event.target.value.toUpperCase().trim())}
                                            value={formik.values.reservation_code_2}
                                            helperText={formik.touched.reservation_code_2 && formik.errors.reservation_code_2}
                                            error={!!formik.touched.reservation_code_2 && !!formik.errors.reservation_code_2}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <NumericInput
                                            label="Passageiros na reserva (CPFs)"
                                            name="pax"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.pax}
                                            helperText={formik.touched.pax && formik.errors.pax}
                                            error={!!formik.touched.pax && !!formik.errors.pax}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input
                                            label="Nome do PAX"
                                            name="passenger_name"
                                            value={formik.values.passengers?.[0]?.name ?? ''}
                                            onChange={(e) => {
                                                const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                                formik.setFieldValue('passengers', [{ ...current, name: e.target.value }], true)
                                            }}
                                            onBlur={() => {
                                                formik.setFieldTouched('passengers', true)
                                            }}
                                            helperText={
                                                (formik.touched.passengers || formik.submitCount > 0) &&
                                                typeof formik.errors.passengers === 'string'
                                                    ? formik.errors.passengers
                                                    : undefined
                                            }
                                            error={
                                                !!(formik.touched.passengers || formik.submitCount > 0) &&
                                                !!formik.errors.passengers
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input
                                            label="Sobrenome do PAX"
                                            name="passenger_surname"
                                            value={formik.values.passengers?.[0]?.surname ?? ''}
                                            onChange={(e) => {
                                                const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                                formik.setFieldValue('passengers', [{ ...current, surname: e.target.value }], true)
                                            }}
                                            onBlur={() => {
                                                formik.setFieldTouched('passengers', true)
                                            }}
                                            error={
                                                !!(formik.touched.passengers || formik.submitCount > 0) &&
                                                !!formik.errors.passengers
                                            }
                                        />
                                    </Grid>
                                    {requiresPassengerBirthDate(formik.values.company || bid?.data?.company) && (
                                        <Grid item xs={12} sm={6} md={4}>
                                            <DatePicker
                                                label="Data de Nascimento do PAX"
                                                value={momentFromCalendarDate(formik.values.passengers?.[0]?.birth_date)}
                                                onChange={(value) => {
                                                    const current = formik.values.passengers?.[0] ?? { name: '', surname: '', birth_date: '' }
                                                    formik.setFieldValue(
                                                        'passengers',
                                                        [{
                                                            ...current,
                                                            birth_date: value?.isValid() ? value.format('YYYY-MM-DD') : '',
                                                        }],
                                                        true
                                                    )
                                                    formik.setFieldTouched('passengers', true)
                                                }}
                                                helperText={
                                                    (formik.touched.passengers || formik.submitCount > 0) &&
                                                    typeof formik.errors.passengers === 'string'
                                                        ? formik.errors.passengers
                                                        : undefined
                                                }
                                                error={
                                                    !!(formik.touched.passengers || formik.submitCount > 0) &&
                                                    !!formik.errors.passengers
                                                }
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Input
                                            label="Aeroporto de Origem"
                                            name="origin_airport_code"
                                            onChange={e => formik.setFieldValue('origin_airport_code', e.target.value.replace(/([^A-Z])/ig, '').toLocaleUpperCase().slice(0, 3), true)}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.origin_airport_code}
                                            helperText={formik.touched.origin_airport_code && formik.errors.origin_airport_code}
                                            error={!!formik.touched.origin_airport_code && !!formik.errors.origin_airport_code}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Detalhes do pagamento</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <NumericInput
                                            label="Milhas"
                                            name="amount"
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.amount}
                                            helperText={formik.touched.amount && formik.errors.amount}
                                            error={!!formik.touched.amount && !!formik.errors.amount}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <CurrencyInput
                                            label="Milheiro Vendido"
                                            name="cost_per_k"
                                            onChangeValue={(_, originalValue) => {
                                                formik.setFieldValue('cost_per_k', originalValue, true)
                                            }}
                                            value={formik.values.cost_per_k}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <CurrencyInput
                                            label="Taxas (R$)"
                                            name="fees"
                                            onChangeValue={(_, originalValue) => formik.setFieldValue('fees', originalValue, true)}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.fees}
                                            helperText={formik.touched.fees && formik.errors.fees}
                                            error={!!formik.touched.fees && !!formik.errors.fees}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip title="Bagagem despachada, marcação de assentos, etc...">
                                            <div>
                                                <CurrencyInput
                                                    label="Custos Adicionais (R$)"
                                                    name="additional_cost"
                                                    onChangeValue={(_, originalValue) => formik.setFieldValue('additional_cost', originalValue, true)}
                                                    onBlur={formik.handleBlur}
                                                    value={formik.values.additional_cost}
                                                    helperText={formik.touched.additional_cost && formik.errors.additional_cost}
                                                    error={!!formik.touched.additional_cost && !!formik.errors.additional_cost}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <CurrencyInput
                                            label="Valor de Venda (R$)"
                                            name="sell_price"
                                            onChangeValue={(_, originalValue) => {
                                                const milesCost = currency(originalValue).subtract(fees).subtract(additional_cost).value

                                                const miles = currency(amount).divide(1000).value

                                                const costPerK = miles ? currency(milesCost).divide(miles).value : ''

                                                formik.setFieldValue('cost_per_k', costPerK)

                                                formik.setFieldValue('sell_price', originalValue, true)
                                            }}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.sell_price}
                                            helperText={formik.touched.sell_price && formik.errors.sell_price}
                                            error={!!formik.touched.sell_price && !!formik.errors.sell_price}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Tooltip title="Cartão utilizado para pagamento das taxas." placement="top">
                                            <div>
                                                <Autocomplete 
                                                    label="Cartão"
                                                    onChange={({value}) => formik.setFieldValue('card_id', value, true)}
                                                    options={CARDS_OPTIONS}
                                                    onBlur={formik.handleBlur}
                                                    value={CARDS_OPTIONS.find((item: { value: string }) => item.value === formik.values.card_id) || null}
                                                    helperText={formik.touched.card_id && formik.errors.card_id}
                                                    error={!!formik.touched.card_id && !!formik.errors.card_id}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={12} sx={{mt: 1}}>
                                        <Typography variant="subtitle1" fontWeight={500} gutterBottom>Informações Adicionais</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Input 
                                            label="Observação"
                                            name="description"
                                            onChange={formik.handleChange}
                                            value={formik.values.description}
                                            helperText={formik.touched.description && formik.errors.description}
                                            error={!!formik.touched.description && !!formik.errors.description}
                                            onBlur={formik.handleBlur}
                                            multiline
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={11}>
                                        <Input 
                                            label="HP"
                                            name="hp_id"
                                            onChange={formik.handleChange}
                                            value={formik.values.hp_id}
                                            helperText={formik.touched.hp_id && formik.errors.hp_id}
                                            error={!!formik.touched.hp_id && !!formik.errors.hp_id}
                                            onBlur={formik.handleBlur}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={1}>
                                        <Button 
                                        style={{
                                            height: '100%'
                                        }}
                                        fullWidth
                                            onClick={async () => {
                                                const {status} = await validateHP.post('/order/validate-hp', {
                                                    hp_id: formik.values.hp_id,
                                                    customer_id: formik.values.customer_id
                                                })

                                                if (status < 299) {
                                                    enqueueSnackbar('HP válido!', {variant: 'success'})
                                                }
                                            }}
                                            disabled={!formik.values.customer_id || formik.values.hp_id.length !== 12 || !formik.values.hp_id.toLowerCase().startsWith('hp')}
                                        >
                                            <Done />
                                        </Button>
                                    </Grid>
                                    {/* <Grid item xs={12}>
                                        <Autocomplete 
                                            label="Cupom"
                                            onChange={({value}) => formik.setFieldValue('coupon_id', value, true)}
                                            options={couponsOptions}
                                            onBlur={formik.handleBlur}
                                            value={couponsOptions.find(item => item.value === formik.values.coupon_id)}
                                            helperText={formik.touched.coupon_id && formik.errors.coupon_id}
                                            error={!!formik.touched.coupon_id && !!formik.errors.coupon_id}
                                        />
                                    </Grid> */}

                                    <Grid item xs={12} sm={6} md={4}>
                                        <FormControlLabel 
                                            control={
                                                <Checkbox 
                                                    checked={formik.values.to_receive}
                                                    onChange={event => formik.setFieldValue('to_receive', event.target.checked)}
                                                    name="to_receive" 
                                                />
                                            } 
                                            label="Á receber" 
                                        />
                                        {formik.values.to_receive && (
                                            <DatePicker 
                                                label="Data recebimento"
                                                value={formik.values.income_due_date ? moment(formik.values.income_due_date) : null}
                                                onChange={(value) => formik.setFieldValue('income_due_date', value?.toDate(), true)}
                                                helperText={formik.touched.income_due_date && formik.errors.income_due_date}
                                                error={!!formik.touched.income_due_date && !!formik.errors.income_due_date}
                                            />
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                                        Resumo
                                                    </Typography>
                                                </Grid>
                                                
                                                <Grid item xs={12}>
                                                    <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1.5,
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}>
                                                            <Typography  fontWeight={500}>Custo Total:</Typography>
                                                            <Typography  color="text.secondary">{formatMoney(totalCost)}</Typography>
                                                        </Box>
                                                        {/* <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}>
                                                            <Typography  fontWeight={500}>Desconto:</Typography>
                                                            <Typography  color="text.secondary">{formatMoney(couponDiscount)}</Typography>
                                                        </Box> */}

                                                        {me?.role === 'ADMIN' && (<Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            borderBottom: '1px solid #e0e0e0'
                                                        }}>
                                                            <Typography  fontWeight={500}>RAM:</Typography>
                                                            <Typography color="text.secondary">
                                                                {formatMoney(currency(formik.values.sell_price).subtract(totalCost).subtract(couponDiscount).value)}
                                                            </Typography>
                                                        </Box>)}


                                                        <Box sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            p: 1
                                                        }}>
                                                            <Typography fontWeight={500}>Milheiro Vendido:</Typography>
                                                            <Typography color="text.secondary">
                                                                {formatMoney(formik.values.sell_price  && formik.values.amount ? soldCostPerK : 0)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12} marginTop={2}>
                                        <Button 
                                            type="submit"
                                            variant="contained" 
                                            color="primary" 
                                            fullWidth 
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Processando...' : 'Finalizar Pedido'}
                                        </Button>
                                    </Grid>

                                </Grid>
                            </form>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} style={{border: '1px solid #e0e0e0'}}>
                            <Grid container padding={2} spacing={2}>
                                <Grid item xs={12} marginBottom={2}>
                                    <Typography textAlign={"center"} variant="h5">Detalhes da Oferta</Typography>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={2}>
                                                <Typography fontWeight="bold">
                                                    ID:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={10}>
                                                <Link href={`/oferta/${bid?.data?.id}`}>
                                                    # {bid?.data?.id}
                                                </Link>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={2}>
                                                <Typography fontWeight="bold">
                                                    Data:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={10}>
                                                <Typography>
                                                    {moment(bid?.data?.created_at).format('DD/MM/YYYY HH:mm')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Fornecedor:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.user?.name}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item  xs={4} >
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Companhia:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {COMPANY_OPTIONS.find(item => item.value === bid?.data?.company)?.label}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item  xs={4}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Quantidade:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography color={usedMiles > 0 ? 'red' : 'black'}>
                                                    {Number(currency(bid?.data?.amount).subtract(usedMiles).value).toLocaleString('pt-BR')}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item  xs={4}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                CPFs Disponíveis:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                                <Typography color={usedPax > 0 ? 'red' : 'black'}>
                                                {bid?.data?.pax === null ? '∞' : currency(Number(bid?.data?.pax)).subtract(usedPax).value}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                Custo do Milheiro:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography>
                                                R$ {Number(bid?.data?.price_per_k).toFixed(2)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container direction="column">
                                        <Grid item xs={12}>
                                            <ButtonGroup fullWidth>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<WhatsAppIcon />}
                                                    onClick={() => 
                                                        window.open(`https://api.whatsapp.com/send?phone=${bid?.data?.user?.whatsapp}&text=${messageText}`, '_blank')
                                                    }
                                                    sx={{borderRadius: 0}}
                                                    disabled={!bid?.data?.user?.whatsapp}
                                                >
                                                    WHATSAPP
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    startIcon={<TelegramIcon />}
                                                    onClick={() => 
                                                        window.open(`https://t.me/${bid?.data?.user?.telegram.replace('@', '')}?text=${messageText}`, '_blank')
                                                    }
                                                    disabled={!bid?.data?.user?.telegram}
                                                    sx={{borderRadius: 0}}
                                                >
                                                    TELEGRAM
                                                </Button>
                                            </ButtonGroup>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                        <Paper elevation={1} style={{ marginTop: 30, border: '1px solid #e0e0e0' }}>
                            <Grid container padding={2} spacing={2}>
                                <Grid item xs={12} marginBottom={2}>
                                    <Typography textAlign={"center"} variant="h5">Dados da Conta</Typography>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>

                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Login:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {bid?.data?.account?.login}
                                                    <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                        navigator?.clipboard?.writeText(bid?.data?.account?.login)
                                                        enqueueSnackbar('Copiado!', {variant: 'success'})
                                                    }}>
                                                        <ContentCopy />
                                                    </IconButton>
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Senha:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                {
                                                    passwordData ? (
                                                        <Fragment>
                                                            {passwordData.password}
                                                            <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                                navigator?.clipboard?.writeText(passwordData.password)
                                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                                            }}>
                                                                <ContentCopy />
                                                            </IconButton>
                                                        </Fragment>
                                                    ) : (
                                                    <div style={{cursor: 'pointer'}} onClick={handleRevealPassword}>
                                                        <VisibilityIcon fontSize="inherit" />
                                                    </div>
                                                    )
                                                }
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    E-mail do Titular:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography component={"span"}>
                                                    {bid?.data?.account?.holder_email}
                                                </Typography>
                                                <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                    navigator?.clipboard?.writeText(bid?.data?.account?.holder_email)
                                                    enqueueSnackbar('Copiado!', {variant: 'success'})
                                                }}>
                                                    <ContentCopy />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Grid container direction="column">
                                            <Grid item xs={4}>
                                                <Typography fontWeight="bold">
                                                    Nome do Titular:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography component={"span"}>
                                                    {bid?.data?.account?.holder_name}
                                                </Typography>
                                                <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                    navigator?.clipboard?.writeText(bid?.data?.account?.holder_name)
                                                    enqueueSnackbar('Copiado!', {variant: 'success'})
                                                }}>
                                                    <ContentCopy />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                
                                <Grid container direction="row" spacing={2} paddingX={2} paddingY={0.5}>
                                <Grid item xs={6}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                CPF do Titular:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography component={"span"}>
                                                {formatCPF(bid?.data?.account?.holder_cpf || '')}
                                            </Typography>
                                            <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                navigator?.clipboard?.writeText(bid?.data?.account?.holder_cpf)
                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                            }}>
                                                <ContentCopy />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={6}>
                                    <Grid container direction="column">
                                        <Grid item xs={4}>
                                            <Typography fontWeight="bold">
                                                Data de Nasc. do Titular:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography component={"span"}>
                                                {formatDate(bid?.data?.account?.holder_birthday || '')}
                                            </Typography>
                                            <IconButton size="small" sx={{ml: 1}} color="primary" onClick={() => { 
                                                navigator?.clipboard?.writeText(formatDate(bid?.data?.account?.holder_birthday || ''))
                                                enqueueSnackbar('Copiado!', {variant: 'success'})
                                            }}>
                                                <ContentCopy />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                </Grid>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {formik.values.card_id && (
                <CreditCardModal
                    open={creditCardModalVisibility}
                    onClose={creditCardModalVisibilityControls.setFalse} 
                    cardId={Number(formik.values.card_id)}
                />
            )}
            {createCustomerModalVisibility && (
                <CreateCustomerModal 
                    open={createCustomerModalVisibility}
                    onClose={createCustomerModalVisibilityControls.setFalse}
                    onSubmit={async (customerId) => {
                        await refetchCustomers()
                        formik.setFieldValue('customer_id', customerId)
                    }}
                />
            )}
        </Layout>
    )
}